import React from "react";
import { Link } from "react-router-dom";
import { BOOKING_URL } from "@/lib/site";
import { BookLink } from "@/components/site/BookLink";
import { PageHero, Eyebrow } from "@/components/site/Section";
import { trackEvent } from "@/lib/analytics";
import { SERVICE_CATEGORIES, SERVICES } from "@/lib/content";
import { useContent } from "@/context/ContentContext";

export default function Services() {
  const { services = SERVICES } = useContent();
  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Our Services"
        title="Services Designed Around You"
        subtitle="Explore injectables, skin rejuvenation, body contouring, wellness, and weight-management services guided by personalized consultation and natural-looking results."
      />

      {/* Categories */}
      <section className="py-16 bg-[var(--cream)]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICE_CATEGORIES.map((c) => (
            <div key={c.name} className="soft-card rounded-2xl p-6">
              <h3 className="font-display text-2xl text-[var(--charcoal)]">{c.name}</h3>
              <p className="text-sm text-[var(--charcoal-soft)] mt-2 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service list */}
      <section className="pb-8 bg-[var(--cream)]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10 space-y-12">
          {services.map((s, i) => (
            <div
              key={s.name}
              data-testid={`service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="rounded-2xl overflow-hidden soft-card">
                <img src={s.img} alt={s.name} className="w-full h-72 object-cover" />
              </div>
              <div>
                <Eyebrow className="mb-2">{s.tag}</Eyebrow>
                <h2 className="font-display text-4xl text-[var(--charcoal)] leading-tight">{s.name}</h2>
                <p className="text-[var(--gold)] italic font-display text-lg mt-1">{s.tagline}</p>
                <p className="text-[var(--charcoal-soft)] leading-relaxed mt-4">{s.desc}</p>
                <div className="flex items-center gap-4 mt-5">
                  <span className="text-sm text-[var(--charcoal-soft)]">{s.duration}</span>
                  <span className="font-display text-2xl text-[var(--charcoal)]">{s.price}</span>
                  {s.sale && (
                    <span className="text-[0.65rem] uppercase tracking-wider bg-[var(--gold)] text-white rounded-full px-3 py-1">{s.saleLabel || "Sale"}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <BookLink
                    label={s.name}
                    href={BOOKING_URL}
                    onClick={() => trackEvent("service_click", s.name)}
                    className="btn-gold rounded-full px-7 py-3"
                  >
                    Book Now
                  </BookLink>
                  {s.name.includes("Weight") && (
                    <Link to="/glp1" onClick={() => trackEvent("service_click", "Explore Plans")} className="btn-outline-gold rounded-full px-7 py-3">
                      Explore Plans
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-[var(--charcoal-soft)] mt-12 max-w-2xl mx-auto px-5">
          Treatment pricing and eligibility may vary. Final recommendations and pricing are confirmed during consultation.
        </p>
      </section>

      {/* Cherry financing band */}
      <section className="py-20 bg-[var(--cream-2)] border-y border-[var(--line)]">
        <div className="max-w-[900px] mx-auto px-5 lg:px-10 text-center">
          <Eyebrow className="mb-3">Payment Plans Available</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--charcoal)]">Cherry Financing</h2>
          <p className="text-[var(--charcoal-soft)] mt-4">
            Cherry payment plans are available as the primary financing option for Seraphina Collective.
          </p>
          <Link to="/financing" data-testid="services-view-financing" className="btn-gold rounded-full px-8 py-3.5 inline-block mt-7">
            View Financing
          </Link>
        </div>
      </section>
    </div>
  );
}
